import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, fmtINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  MagnifyingGlass, PaperPlaneTilt, ArrowsClockwise, CheckCircle, Copy, User, Wrench, CurrencyInr,
} from "@phosphor-icons/react";

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{value || "—"}</span>
  </div>
);

export default function CollectPayment() {
  const [params] = useSearchParams();
  const [bid, setBid] = useState(params.get("bid") || "");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const lookup = async (id) => {
    const q = (id ?? bid).trim();
    if (!q) { toast.error("Enter a Booking ID"); return; }
    setBusy(true); setJob(null);
    try {
      const { data } = await api.get(`/collect-payment/lookup/${q}`);
      setJob(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (params.get("bid")) lookup(params.get("bid")); /* eslint-disable-next-line */ }, []);

  const sendLink = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/collect-payment/${job.id}/send-link`);
      toast.success(`Payment link sent to ${data.notified_email || ""} ${data.notified_phone || ""}`);
      setJob({ ...job, balance_link: data.balance_link });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/collect-payment/${job.id}/refresh`);
      setJob(data.job);
      toast.success(data.status === "paid" ? "Balance collected! 🎉" : `Status: ${data.status}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const link = job?.balance_link;

  const [charge, setCharge] = useState({ amount: "", description: "" });
  const [addingCharge, setAddingCharge] = useState(false);
  const addCharge = async () => {
    const amt = Number(charge.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setAddingCharge(true);
    try {
      const { data } = await api.post(`/collect-payment/${job.id}/add-charge`, { amount: amt, description: charge.description });
      setJob(data);
      setCharge({ amount: "", description: "" });
      toast.success(`Added ₹${amt} — send the payment link to collect it`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAddingCharge(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="collect-payment-page">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <CurrencyInr size={26} weight="bold" className="text-primary" /> Collect Payment
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Enter a Booking ID to send the customer a secure Razorpay payment link (via email &amp; SMS) for any outstanding balance.</p>
        </div>

        <div className="flex gap-2">
          <Input value={bid} onChange={(e) => setBid(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="Enter Booking ID (e.g. acd2b1aa-...)" className="rounded-sm" data-testid="cp-booking-input" />
          <Button onClick={() => lookup()} disabled={busy} className="rounded-sm gap-1.5" data-testid="cp-lookup-btn">
            <MagnifyingGlass size={16} /> Look up
          </Button>
        </div>

        {job && (
          <div className="grid md:grid-cols-2 gap-5" data-testid="cp-details">
            <div className="bg-card border border-border rounded-sm p-5">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><User size={17} weight="bold" /> Customer Details</h3>
              <Field label="Name" value={job.customer_name} />
              <Field label="Email" value={job.customer_email} />
              <Field label="Phone" value={job.customer_phone} />
              <Field label="Address" value={job.address} />
            </div>
            <div className="bg-card border border-border rounded-sm p-5">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><Wrench size={17} weight="bold" /> Service Details</h3>
              <Field label="Service" value={`${job.product_category} · ${job.service_type}`} />
              <Field label="Date / Slot" value={`${job.scheduled_date || "—"} ${job.slot || ""}`} />
              <Field label="Booking Status" value={job.status} />
              <Field label="Payment Mode" value={job.payment_mode} />
            </div>

            <div className="md:col-span-2 bg-card border border-border rounded-sm p-5">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><CurrencyInr size={17} weight="bold" /> Payment</h3>
              <div className="grid grid-cols-3 gap-3 text-center mb-4">
                <div className="bg-secondary/40 rounded-sm p-3"><p className="text-xs text-muted-foreground">Total</p><p className="font-bold font-mono">{fmtINR(job.amount)}</p></div>
                <div className="bg-green-50 rounded-sm p-3"><p className="text-xs text-green-700">Paid</p><p className="font-bold font-mono text-green-700">{fmtINR(job.paid_amount)}</p></div>
                <div className={`rounded-sm p-3 ${job.balance_due > 0 ? "bg-amber-50" : "bg-secondary/40"}`}><p className={`text-xs ${job.balance_due > 0 ? "text-amber-700" : "text-muted-foreground"}`}>Balance</p><p className={`font-bold font-mono ${job.balance_due > 0 ? "text-amber-700" : ""}`} data-testid="cp-balance">{fmtINR(job.balance_due)}</p></div>
              </div>

              {job.additional_charges?.length > 0 && (
                <div className="mb-4 border border-border rounded-sm divide-y divide-border" data-testid="cp-additional-charges">
                  <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Additional charges (audit)</div>
                  {job.additional_charges.map((c) => (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="min-w-0"><span className="font-medium">{fmtINR(c.amount)}</span> · <span className="text-muted-foreground">{c.description}</span></span>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${c.paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{c.paid ? "Collected" : "Pending"}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-4 rounded-sm border border-dashed border-border p-3" data-testid="cp-add-charge">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Add additional charge</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input type="number" min="1" value={charge.amount} onChange={(e) => setCharge({ ...charge, amount: e.target.value })} placeholder="Amount ₹" className="rounded-sm sm:w-32" data-testid="cp-charge-amount" />
                  <Input value={charge.description} onChange={(e) => setCharge({ ...charge, description: e.target.value })} placeholder="Reason (e.g. extra gas refill)" className="rounded-sm flex-1" data-testid="cp-charge-desc" />
                  <Button variant="outline" onClick={addCharge} disabled={addingCharge} className="rounded-sm gap-1.5" data-testid="cp-add-charge-btn"><CurrencyInr size={15} /> {addingCharge ? "Adding…" : "Add"}</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Adds to the balance due. Then use "Send Payment Link" below to collect it — even for prepaid bookings.</p>
              </div>

              {job.balance_due > 0 ? (
                <>
                  {!link ? (
                    <Button onClick={sendLink} disabled={sending} className="rounded-sm w-full h-11 gap-2" data-testid="cp-send-link-btn">
                      <PaperPlaneTilt size={17} weight="bold" /> {sending ? "Sending…" : `Send Payment Link (${fmtINR(job.balance_due)})`}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className={`rounded-sm p-3 text-sm flex items-center gap-2 ${link.status === "paid" ? "bg-green-50 border border-green-200 text-green-800" : "bg-blue-50 border border-blue-200 text-blue-800"}`}>
                        {link.status === "paid" ? <CheckCircle size={18} weight="fill" /> : <PaperPlaneTilt size={16} />}
                        <span>Link {link.status === "paid" ? "PAID ✓" : "sent"} · {fmtINR(link.amount)}{link.notified?.sms ? " · SMS" : ""}{link.notified?.email ? " · Email" : ""}</span>
                      </div>
                      {link.short_url && (
                        <div className="flex gap-2 items-center">
                          <Input readOnly value={link.short_url} className="rounded-sm font-mono text-xs" data-testid="cp-link-url" />
                          <Button variant="outline" size="icon" className="rounded-sm shrink-0" onClick={() => copyLink(link.short_url)} data-testid="cp-copy-link"><Copy size={16} /></Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={refresh} disabled={busy} className="rounded-sm gap-1.5 flex-1" data-testid="cp-refresh-btn"><ArrowsClockwise size={15} /> Check Status</Button>
                        <Button onClick={sendLink} disabled={sending} className="rounded-sm gap-1.5 flex-1" data-testid="cp-resend-btn"><PaperPlaneTilt size={15} /> Resend Link</Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm text-green-800 flex items-center gap-2" data-testid="cp-no-balance">
                  <CheckCircle size={18} weight="fill" /> This booking is fully paid — no balance to collect.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
}
